import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { CatalogListElement, ProductService } from '../shared/services/catalog.service';
import { HcSort, HcTableDataSource, HcToastOptions, HcToasterService, PaginationComponent } from '@healthcatalyst/cashmere';
import { TypeService } from '../shared/services/type.service';
import { ProductLineService } from '../shared/services/product-line.service';
import { StatusService } from '../shared/services/status.service';
import { SLAService } from '../shared/services/sla.service';
import { OktaService } from '../shared/services/okta.service';
import { environment } from '../../environments/environment';

/** Public facing product catalog  */
@Component({
    selector: 'pd-catalog',
    templateUrl: './catalog.component.html',
    styleUrls: ['./catalog.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class CatalogComponent implements OnInit, AfterViewInit, OnDestroy {
    private unsubscribe = new Subject<void>();
    dataSource: HcTableDataSource<any> = new HcTableDataSource();
    productList: any[] = [];
    loading = true;
    loadError = false;
    productsDisplayedColumns: string[] = [
        'item',
        'name',
        'category',
        'type',
        'productLine',
        'status',
        'sla',
        'revisionDate',
        'actions'
    ];
    productsPageSize: number = 10;
    filterData: string[] = [];
    filterSelections: string[] = [];
    filterType = '';
    categoryFilters: string[] = [];
    typeFilters: string[] = [];
    productLineFilters: string[] = [];
    statusFilters: string[] = [];
    publishStatusFilters: string[] = ['Published'];
    categoryText = 'View All';
    typeText = 'View All';
    productLineText = 'View All';
    statusText = 'View All';
    publishStatusText = 'Published';
    typeKey: { [key: string]: string } = {};
    productLineKey: { [key: string]: string } = {};
    statusKey: { [key: string]: string } = {};
    slaKey: { [key: string]: string } = {};

    @ViewChild(HcSort)
    sort: HcSort;

    @ViewChild(PaginationComponent)
    paginator: PaginationComponent;

    get productsListLength(): number {
        return this.dataSource.filteredData ? this.dataSource.filteredData.length : 0;
    }

    constructor(
        private productService: ProductService,
        private toasterService: HcToasterService,
        private typeService: TypeService,
        private productLineService: ProductLineService,
        private statusService: StatusService,
        private slaService: SLAService,
        public oktaService: OktaService
    ) { }

    ngOnInit(): void {
        this.typeService.allTypeKey.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.typeKey = list;
                if (this.productLineKey && this.statusKey && this.slaKey && this.oktaService.superAdmin.value !== null) {
                    this.loadCatalog();
                }
            } else {
                this.typeService.loadAllTypes();
            }
        });

        this.productLineService.productLineKeyUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.productLineKey = list;
                if (this.typeKey && this.statusKey && this.slaKey && this.oktaService.superAdmin.value !== null) {
                    this.loadCatalog();
                }
            } else {
                this.productLineService.refresh();
            }
        });

        this.statusService.statusKeyUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.statusKey = list;
                if (this.typeKey && this.productLineKey && this.slaKey && this.oktaService.superAdmin.value !== null) {
                    this.loadCatalog();
                }
            } else {
                this.statusService.refresh();
            }
        });

        this.slaService.slaKeyUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.slaKey = list;
                if (this.typeKey && this.productLineKey && this.statusKey && this.oktaService.superAdmin.value !== null) {
                    this.loadCatalog();
                }
            } else {
                this.slaService.refresh();
            }
        });

        this.oktaService.superAdminUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
            if (this.typeKey && this.productLineKey && this.statusKey && this.oktaService.superAdmin.value !== null) {
                this.loadCatalog();
            }
        });
    }

    ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;

        setTimeout(() => {
            this.initSorting();
        });   
    }

    loadCatalog(): void {
        this.productService.catalogUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                let tableList: any[] = list;

                this.loading = false;
                this.loadError = false;
                this.productList = [];

                // Parse the id fields for table content
                tableList.forEach(element => {
                    element['type'] = this.formatID( element['typeID'], 'typeID');
                    element['productLine'] = this.formatID( element['productLineID'], 'productLineID');
                    element['status'] = this.formatID( element['statusID'], 'statusID');
                    element['sla'] = this.formatID( element['slaid'], 'slaid');

                    if ( element['category'] === 'partner' ) {
                        element['category'] = '3rd Party Technology';
                    } else {
                        element['category'] = this.titleCase( element['category'] );
                    }

                    // Filter out products with a discontinued status for all but admins
                    if ( element['status'] !== 'Discontinued' || this.oktaService.admin ) {
                        this.productList.push( element );
                    }
                });

                this.dataSource = new HcTableDataSource(this.productList);
                if (this.sort && this.paginator) {
                    this.dataSource.sort = this.sort;
                    this.dataSource.paginator = this.paginator;
                    this.initSorting();
                }
            } else {
                this.loadError = this.productService.loadFailed;
                if (!this.loadError) {
                    this.productService.refresh();
                } else {
                    this.loading = false;
                }
            }
        });
    }

    initSorting(): void {
        this.dataSource.sortingDataAccessor = (item, property) => {
            switch (property) {     
                case 'revisionDate': {
                    const sortDate = new Date(item.revisionDate);
                    return sortDate.getTime();
                }
                default: {
                    return item[property];
                }
            }
        };
        this.filterProducts();
    }

    formatID(id: number, type: string) {
        let reformattedID: string = '';

        if ( id ) {
            if (type === "typeID") {
                reformattedID = this.typeKey[id.toString()];
            } else if (type === "productLineID") {
                reformattedID = this.productLineKey[id.toString()];
            } else if (type === 'statusID') {
                reformattedID = this.statusKey[id.toString()];
            } else if (type === 'slaid') {
                reformattedID = this.slaKey[id.toString()];
            }
        }

        return reformattedID;
    }

    applyTextFilter(text: string): void {
        this.dataSource.filter = text.trim().toLowerCase();
    }

    storeSelectedTextValue(filterArray: string[]) {
        switch (this.filterType) {
            case 'category':
                if (filterArray.length == 1) this.categoryText = filterArray[0][0].toUpperCase() + filterArray[0].slice(1);
                else if (filterArray.length > 1) this.categoryText = `${filterArray.length} selected`;
                else this.categoryText = 'View All';
                break;
            case 'type':
                if (filterArray.length == 1) this.typeText = filterArray[0];
                else if (filterArray.length > 1) this.typeText = `${filterArray.length} selected`;
                else this.typeText = 'View All';
                break;
            case 'status':
                if (filterArray.length == 1) this.statusText = filterArray[0];
                else if (filterArray.length > 1) this.statusText = `${filterArray.length} selected`;
                else this.statusText = 'View All';
                break;
            case 'productLine':
                if (filterArray.length == 1) this.productLineText = filterArray[0];
                else if (filterArray.length > 1) this.productLineText = `${filterArray.length} selected`;
                else this.productLineText = 'View All';
                break;
            case 'published':
                if (filterArray.length == 1) this.publishStatusText = filterArray[0];
                else if (filterArray.length > 1) this.publishStatusText = `${filterArray.length} selected`;
                else this.publishStatusText = 'View All';
                break;
            default:
                break;
        }
    }

    storeFilterData(type: string) {
        let filterData: string[] = [];
        this.filterType = type;

        //add clean strings for publish status
        if (type === 'published') {
            filterData.push('Published');
            filterData.push('Unpublished');
        } else {
            for (let i = 0; i < this.productList.length; i++) {
                if (this.productList[i][type]) {
                    if (type !== 'category' && !filterData.includes(this.productList[i][type])) {
                        filterData.push(this.productList[i][type]);
                    } else if (type === 'category' && !filterData.includes(this.productList[i][type])) {
                        filterData.push(this.productList[i][type]);
                    }
                }
            }
        }
        this.filterData = filterData;
    }

    filterProducts() {
        let filteredProducts: CatalogListElement[] = [];

        for (let i = 0; i < this.productList.length; i++) {
            let categoryFilterMatch = this.categoryFilters.length === 0;
            if (this.categoryFilters.includes(this.productList[i]['category'])) {
                categoryFilterMatch = true;
            }

            let typeFilterMatch = this.typeFilters.length === 0;
            if (this.productList[i]['type'] && this.typeFilters.includes(this.productList[i]['type'])) {
                typeFilterMatch = true;
            }

            let statusFilterMatch = this.statusFilters.length === 0;
            if (this.productList[i]['status'] && this.statusFilters.includes(this.productList[i]['status'])) {
                statusFilterMatch = true;
            }

            let productLineFilterMatch = this.productLineFilters.length === 0;
            if (this.productList[i]['productLine'] && this.productLineFilters.includes(this.productList[i]['productLine'])) {
                productLineFilterMatch = true;
            }

            // check string filter values from filter popoover against boolean values from data
            let publishStatusFilterMatch = this.publishStatusFilters.length === 0;
            if (
                (this.publishStatusFilters.includes('Published') && this.productList[i]['published'] === true) ||
                (this.publishStatusFilters.includes('Unpublished') && this.productList[i]['published'] === false)
            ) {
                publishStatusFilterMatch = true;
            }

            if (categoryFilterMatch && typeFilterMatch && statusFilterMatch && productLineFilterMatch && publishStatusFilterMatch) {
                filteredProducts.push(this.productList[i]);
            }
        }

        //grab filter that was applied from text
        let textFilter = this.dataSource.filter;

        //set data source to the filtered product list
        this.dataSource = new HcTableDataSource(filteredProducts);

        //reset paginator and sort
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;

        //add the text filter
        this.dataSource.filter = textFilter;
    }

    storeFilterArray(filterArray: string[]) {
        this.storeSelectedTextValue(filterArray);

        if (this.filterType === 'category') {
            this.categoryFilters = filterArray;
        } else if (this.filterType === 'type') {
            this.typeFilters = filterArray;
        } else if (this.filterType === 'productLine') {
            this.productLineFilters = filterArray;
        } else if (this.filterType === 'status') {
            this.statusFilters = filterArray;
        } else if (this.filterType === 'published') {
            this.publishStatusFilters = filterArray;
        }

        this.filterProducts();
    }

    openDS(id: number, download: boolean): void {
        let link = '/viewer?';
        download ? link += 'download=true&id=' : link += 'id=';
        window.open(link + id.toString(), '_blank');
    }

    generateLink(id: number, name: string) {
        const link = environment.root.url + '/viewer?id=' + id.toString();
        navigator.clipboard.writeText(link);

        const options: HcToastOptions = {
            header: 'Copied data sheet link for ' + name,
            type: 'info',
            position: 'bottom-center'
        };
        this.toasterService.addToast(options);
    }

    titleCase( val: string ): string {
        return val.charAt(0).toUpperCase() + val.slice(1);
    }
    
    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
